import React, { useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FIELD_PADDING } from '../SettlePayment/const';

const ALL_COMPANIES_VALUE = '__ALL_COMPANIES__';

const CompanyNameAutocomplete = ({ profile, value, onChange }) => {
	const { t } = useTranslation();

	// Only keep active contracts
	const contractList = useMemo(() => {
			return (profile?.agency_contracts || []).filter((c) => c?.active !== false);
	}, [profile]);

	// Create options with "All Companies" at the top
	const options = useMemo(() => {
		const allCompaniesOption = { _id: ALL_COMPANIES_VALUE, company: { name: t('AdvancedFilters.AllCompanies') } };
		return [allCompaniesOption, ...contractList];
	}, [contractList, t]);

	// Find the default object by id, else null
	const getDefaultValue = () => {
		// If value is an array (All Companies), return the All Companies option
		if (Array.isArray(value)) {
			return options.find((c) => c._id === ALL_COMPANIES_VALUE) || null;
		}
		return options.find((c) => c._id === value) || null;
	};

	const handleChange = (e, val) => {
		if (!val) {
			onChange(null);
		} else if (val._id === ALL_COMPANIES_VALUE) {
			// Return all contract IDs as an array
			onChange(contractList.map((c) => c._id));
		} else {
			onChange(val._id);
		}
	};

	const filterOptions = createFilterOptions({
		matchFrom: 'start',
		stringify: (option) => option.company?.name || '',
	});

	const isAllCompaniesSelected = Array.isArray(value);

	return (
		<Autocomplete
			value={getDefaultValue()}
			options={options}
			getOptionLabel={(opt) => opt?.company?.name || ''}
			isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
			filterOptions={filterOptions}
			onChange={handleChange}
			clearOnEscape
			size="small"
			sx={FIELD_PADDING}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder={t('FindByCompanyName')}
					variant="outlined"
					fullWidth
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{(value && !isAllCompaniesSelected) ? (
									<InputAdornment position="end">
										<IconButton onClick={() => onChange?.(null)} disableRipple size="small">
											<CloseIcon />
										</IconButton>
									</InputAdornment>
								) : (
									params.InputProps.endAdornment
								)}
							</>
						),
					}}
				/>
			)}
		/>
	);
};

export default CompanyNameAutocomplete;